import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BellRing, TrendingUp, BarChart3 } from 'lucide-react';
import AdSlot from '../advertisement/AdSlot';

const MAX_GMP = 5;
const MAX_SUBSCRIPTIONS = 4;

const shortName = (name) => String(name || '').replace(/\s+(Ltd\.?|Limited)$/i, '');

// gmpRows: utils/gmpBoard.js board rows; subscriptionRows: utils/subscriptionMapper.js rows
export const NewsDeskSidebar = ({ gmpRows, subscriptionRows, onAlertSignup }) => {
  const [contact, setContact] = useState('');

  const topGmp = gmpRows
    .filter((row) => row.hasGmp && typeof row.gmpPercent === 'number' && !row.isClosed)
    .sort((a, b) => b.gmpPercent - a.gmpPercent)
    .slice(0, MAX_GMP);
  const quotedCount = gmpRows.filter((row) => row.hasGmp).length;

  const topSubscribed = subscriptionRows
    .filter((row) => row.hasData && typeof row.total === 'number')
    .sort((a, b) => b.total - a.total)
    .slice(0, MAX_SUBSCRIPTIONS);
  const maxTotal = topSubscribed.length ? topSubscribed[0].total : 1;

  const handleSubmit = (event) => {
    event.preventDefault();
    setContact('');
    onAlertSignup();
  };

  return (
    <aside className="space-y-5" aria-label="Market trackers and alerts">
      <section aria-labelledby="news-gmp-heading" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2">
          <h2 id="news-gmp-heading" className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
            <TrendingUp className="w-4 h-4 text-[#008080]" aria-hidden="true" />
            Live GMP Tracker
          </h2>
          <span className="px-1.5 py-px rounded border border-emerald-200 bg-emerald-50 text-[10px] font-bold text-emerald-700">Live</span>
        </div>
        {topGmp.length ? (
          <ul className="mt-3 divide-y divide-slate-100">
            {topGmp.map((row) => (
              <li key={row.id}>
                <Link to={row.slug ? `/ipo-gmp/${row.slug}` : '/ipo-gmp'} className="flex items-start justify-between gap-3 py-2.5 group">
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-slate-800 truncate group-hover:text-[#008080]">{shortName(row.name)}</span>
                    <span className="block text-[11px] text-slate-400">
                      {row.price !== null ? `Price ₹${row.price}` : 'Price TBA'} · {row.board}
                    </span>
                  </span>
                  <span className={`text-right text-sm font-extrabold whitespace-nowrap ${row.gmp >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {row.gmp >= 0 ? '+' : '−'}₹{Math.abs(row.gmp)}
                    <span className="block text-[10px] font-semibold">
                      {row.gmpPercent >= 0 ? '+' : ''}
                      {row.gmpPercent.toFixed(1)}%
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-xs text-slate-400">No live grey market quotes right now.</p>
        )}
        <Link to="/ipo-gmp" className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#008080] hover:text-[#006666]">
          View all {quotedCount || ''} tracked IPO premiums
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </section>

      <section aria-labelledby="news-subscription-heading" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
        <h2 id="news-subscription-heading" className="flex items-center gap-2 text-sm font-extrabold text-slate-900">
          <BarChart3 className="w-4 h-4 text-[#008080]" aria-hidden="true" />
          Top Active Subscriptions
        </h2>
        {topSubscribed.length ? (
          <ul className="mt-3 space-y-3">
            {topSubscribed.map((row) => (
              <li key={row.id}>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-bold text-slate-800 truncate">{shortName(row.company)}</span>
                  <span className="font-extrabold text-[#006666] tabular-nums">{row.total.toFixed(2)}x</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-[#008080]" style={{ width: `${Math.max(4, (row.total / maxTotal) * 100)}%` }} />
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-xs text-slate-400">No IPO is open for bidding right now.</p>
        )}
        <Link to="/ipo-subscription" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#008080] hover:text-[#006666]">
          Live subscription status
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </section>



      {/* Partner ad (GET /api/ads): dedicated news slot first, then the shared one */}
      <AdSlot slotId={['ipo-news-sidebar', 'incontent']} variant="compact" />
    </aside>
  );
};

export default NewsDeskSidebar;
